import { useEffect, useState } from "react";
import { Download, FileDown, Filter, TrendingUp } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import ReservationsReportPanel from "@/components/ReservationsReportPanel";
import {
  boliviaDateInputWithOffset,
  boliviaMonthStartInput,
  boliviaTodayInput,
} from "@/lib/dateTime";
import { downloadReportPdf } from "@/lib/reportPdf";

function dateRange(preset) {
  const today = boliviaTodayInput();
  if (preset === "today") return { start: today, end: today };
  if (preset === "week") {
    return { start: boliviaDateInputWithOffset(-6), end: today };
  }
  return { start: boliviaMonthStartInput(), end: today };
}

export default function ReportsPage() {
  const { user } = useAuth();
  const { formatAmount, settings } = useCurrency();
  const money = formatAmount;
  const initial = dateRange("month");
  const [preset, setPreset] = useState("month");
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [seller, setSeller] = useState("");
  const [sellers, setSellers] = useState([]);
  const [report, setReport] = useState(null);

  const loadReport = () => {
    const params = { start_date: start, end_date: end };
    if (seller) {
      params.seller_id = seller;
    }
    api
      .get("/reports/commercial", {
        params,
      })
      .then((response) => setReport(response.data));
  };

  useEffect(() => {
    loadReport();
    if (user.role !== "vendedor") {
      api.get("/auth/users").then((response) => setSellers(response.data));
    }
  }, []);

  const selectPreset = (nextPreset) => {
    setPreset(nextPreset);
    if (nextPreset !== "custom") {
      const range = dateRange(nextPreset);
      setStart(range.start);
      setEnd(range.end);
    }
  };

  const downloadCsv = () => {
    if (!report) return;
    const rows = [
      ["Vendedor", "Cotizaciones", "Monto total", "Inicial promedio", "Descuentos", "Cuota promedio"],
      ...report.por_vendedor.map((item) => [
        item.vendedor,
        item.cotizaciones,
        item.monto_total,
        item.inicial_promedio,
        item.descuentos,
        item.cuota_promedio,
      ]),
    ];
    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "reporte-comercial-puerto-santa-cruz.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const maxAmount = Math.max(...(report?.por_vendedor || []).map((item) => item.monto_total), 1);

  return (
    <section className="page reports-page" data-testid="reports-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">INTELIGENCIA COMERCIAL</span>
          <h1>Reportes de venta</h1>
          <p>Observa el desempeño por vendedor y concentra la actividad en cada periodo.</p>
        </div>
        <div className="report-actions">
          <button className="outline-button" data-testid="report-export-csv-button" onClick={downloadCsv} type="button"><Download size={17} /> CSV</button>
          <button className="primary-button" data-testid="report-export-pdf-button" onClick={() => report && downloadReportPdf(report, settings)} type="button"><FileDown size={17} /> PDF</button>
        </div>
      </header>
      <section className="report-filters" data-testid="report-filters-panel">
        <div className="preset-buttons" data-testid="report-period-presets">
          {[['today', 'Hoy'], ['week', 'Últimos 7 días'], ['month', 'Este mes'], ['custom', 'Personalizado']].map(([key, label]) => <button className={preset === key ? 'selected' : ''} data-testid={`report-preset-${key}`} key={key} onClick={() => selectPreset(key)} type="button">{label}</button>)}
        </div>
        <label htmlFor="report-start">Desde<input data-testid="report-start-date-input" id="report-start" onChange={(e) => setStart(e.target.value)} type="date" value={start} /></label>
        <label htmlFor="report-end">Hasta<input data-testid="report-end-date-input" id="report-end" onChange={(e) => setEnd(e.target.value)} type="date" value={end} /></label>
        {user.role !== "vendedor" && <label htmlFor="report-seller">Vendedor<select data-testid="report-seller-select" id="report-seller" onChange={(e) => setSeller(e.target.value)} value={seller}><option value="">Todo el equipo</option>{sellers.map((item) => <option key={item.id} value={item.id}>{`${item.name} · ${item.email}`}</option>)}</select></label>}
        <button className="dark-button report-filter-button" data-testid="report-filter-button" onClick={loadReport} type="button"><Filter size={17} /> Aplicar filtros</button>
      </section>
      {!report ? <div className="page-loading" data-testid="reports-loading">Cargando reportes...</div> : <><p className="exchange-rate-note" data-testid="report-exchange-rate">1 USD = Bs. {report.moneda.usd_to_bob} · Vista actual: {settings.display_mode === "BOTH" ? "USD y BOB" : settings.display_mode}</p><div className="report-stat-grid" data-testid="report-summary-grid"><article><span>Ventas concretadas</span><strong data-testid="report-quote-count">{report.resumen.cotizaciones}</strong></article><article><span>Monto concretado</span><strong data-testid="report-total-amount">{money(report.resumen.monto_total)}</strong></article><article><span>Descuentos aplicados</span><strong data-testid="report-discounts">{money(report.resumen.descuentos)}</strong></article><article><span>Vendedores activos</span><strong data-testid="report-seller-count">{report.resumen.vendedores}</strong></article></div><ReservationsReportPanel reservations={report.reservas} /><div className="reports-grid"><section className="seller-performance-panel" data-testid="seller-performance-panel"><div className="panel-heading"><div><span className="eyebrow">VENTAS CONCRETADAS</span><h2>Desempeño por vendedor</h2></div><TrendingUp size={20} /></div>{report.por_vendedor.length ? <div className="seller-bars">{report.por_vendedor.map((item) => <div className="seller-bar-row" data-testid={`seller-report-row-${item.vendedor_id}`} key={item.vendedor_id}><div><strong>{item.vendedor}</strong><span>{item.cotizaciones} ventas · {money(item.monto_total)}</span></div><div className="bar-track"><span style={{ width: `${(item.monto_total / maxAmount) * 100}%` }} /></div></div>)}</div> : <p className="empty-inline" data-testid="report-empty-state">No hay ventas concretadas para los filtros seleccionados.</p>}</section><section className="popular-lots-panel" data-testid="popular-lots-panel"><span className="eyebrow">VENTAS</span><h2>Lotes concretados</h2>{report.lotes_consultados.length ? <ol>{report.lotes_consultados.map((item) => <li data-testid={`popular-lot-${item.manzano}-${item.lote}`} key={`${item.manzano}-${item.lote}`}><span><b>{item.manzano} · {item.lote}</b><small>{money(item.monto_total)} concretado</small></span><strong>{item.consultas}</strong></li>)}</ol> : <p className="empty-inline">Sin ventas concretadas en este periodo.</p>}</section></div><section className="report-table-panel" data-testid="report-detail-table-panel"><div className="panel-heading"><div><span className="eyebrow">DETALLE</span><h2>Indicadores de ventas</h2></div></div><div className="data-table-wrap"><table data-testid="report-detail-table"><thead><tr><th>Vendedor</th><th>Ventas</th><th>Monto concreto</th><th>Inicial promedio</th><th>Descuentos</th><th>Cuota promedio</th></tr></thead><tbody>{report.por_vendedor.map((item) => <tr data-testid={`report-table-row-${item.vendedor_id}`} key={item.vendedor_id}><td>{item.vendedor}</td><td>{item.cotizaciones}</td><td>{money(item.monto_total)}</td><td>{money(item.inicial_promedio)}</td><td>{money(item.descuentos)}</td><td>{money(item.cuota_promedio)}</td></tr>)}</tbody></table></div></section></>}
    </section>
  );
}
